/**
 * VoiceHelpButton — one-tap "🔊 Listen" for auth screens (senior accessibility).
 * Fetches cached ElevenLabs narration from the public voice-help endpoint in
 * the site language (hi/mr, else English) and plays it inline.
 */
import { useEffect, useRef, useState } from "react";
import { Volume2, Loader2, Square } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";

const LABELS = {
    en: { listen: "Listen", stop: "Stop", loading: "Loading…" },
    hi: { listen: "सुनें", stop: "रोकें", loading: "रुकिए…" },
    mr: { listen: "ऐका", stop: "थांबवा", loading: "थांबा…" },
};
const audioCache = {};

export function VoiceHelpButton({ helpKey, className = "" }) {
    const { lang } = useLang();
    const voiceLang = lang === "hi" || lang === "mr" ? lang : "en";
    const L = LABELS[voiceLang];
    const [state, setState] = useState("idle"); // idle | loading | playing
    const audioRef = useRef(null);

    const stop = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            audioRef.current = null;
        }
        setState("idle");
    };

    // Stop narration when the step/language changes or on unmount
    useEffect(() => stop, [helpKey, voiceLang]);

    const handle = async () => {
        if (state === "playing") { stop(); return; }
        if (state === "loading") return;
        const ck = `${helpKey}_${voiceLang}`;
        try {
            let url = audioCache[ck];
            if (!url) {
                setState("loading");
                const { data } = await api.post("/public/voice-help", { key: helpKey, lang: voiceLang });
                url = data.audio_data_url;
                audioCache[ck] = url;
            }
            const el = new Audio(url);
            el.addEventListener("ended", () => { audioRef.current = null; setState("idle"); });
            el.addEventListener("error", () => { audioRef.current = null; setState("idle"); });
            audioRef.current = el;
            await el.play();
            setState("playing");
        } catch {
            setState("idle");
        }
    };

    let icon = <Volume2 className="h-4 w-4" />;
    let label = L.listen;
    if (state === "loading") { icon = <Loader2 className="h-4 w-4 animate-spin" />; label = L.loading; }
    else if (state === "playing") { icon = <Square className="h-3.5 w-3.5 fill-current" />; label = L.stop; }

    return (
        <button
            type="button"
            onClick={handle}
            data-testid="voice-help-btn"
            aria-label="Hear these instructions read aloud"
            title="Hear these instructions read aloud"
            className={`inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full font-bold text-[13px] shadow-[0_8px_20px_rgba(245,124,0,0.35)] transition-colors ${
                state === "playing"
                    ? "bg-[#1B5E20] text-white hover:bg-[#144D17]"
                    : "bg-[#F57C00] text-white hover:bg-[#E65100]"
            } ${className}`}
        >
            {icon}
            <span>{label}</span>
        </button>
    );
}

export default VoiceHelpButton;
